import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { askMyJournalStream, indexUserData } from '../store/ai/thunks';
import Draggable from 'react-draggable';
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  useColorMode,
  IconButton,
  CloseButton,
  Spinner,
  Divider,
  Badge,
  useToast,
  Flex,
  Tooltip,
  useBreakpointValue,
  OrderedList,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import { RiDragMove2Line } from "react-icons/ri";
import { BsChatDots } from "react-icons/bs";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isDragGesture } from '../utils/dragGesture';

export default function AIChat({ inline = false, sidebar = false }) {
  const dispatch = useDispatch();
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isIndexing, setIsIndexing] = useState(false);
  const inputRef = useRef(null);
  const chatEndRef = useRef(null);
  const wasDragged = useRef(false);
  const dragOrigin = useRef({ x: 0, y: 0 });

  // Simple helper to summarize sources by type
  const summarizeSources = (citations = []) => {
    if (!citations.length) return null;
    const counts = citations.reduce((acc, c) => {
      const t = (c.type || 'source').toLowerCase();
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([type, count]) => `${count} ${type.toUpperCase()}`)
      .join(', ');
  };

  // Body of a pending entry: streaming markdown when tokens are
  // arriving from the SSE thunk, otherwise the "Thinking..." spinner.
  // Defined inside the component so it can close over aiAnswerStreaming
  // without the parent having to pass it through every render site.
  const renderPendingBody = (hstackExtraProps = {}) => {
    if (aiAnswerStreaming) {
      return (
        <Box fontSize="sm">
          {renderMarkdown(aiAnswerStreaming)}
        </Box>
      );
    }
    return (
      <HStack spacing={2} {...hstackExtraProps}>
        <Spinner size="xs" />
        <Text fontSize="xs">Thinking...</Text>
      </HStack>
    );
  };

  // Markdown renderer (ReactMarkdown + GFM) with Chakra styling
  const renderMarkdown = (text) => {
    if (!text) return null;
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Text fontSize="sm" mb={2}>
              {children}
            </Text>
          ),
          strong: ({ children }) => <Text as="span" fontWeight="bold">{children}</Text>,
          em: ({ children }) => <Text as="span" fontStyle="italic">{children}</Text>,
          ul: ({ children }) => (
            <UnorderedList pl={4} spacing={0} mb={2} mt={0}>
              {children}
            </UnorderedList>
          ),
          ol: ({ children }) => (
            <OrderedList pl={4} spacing={0} mb={2} mt={0}>
              {children}
            </OrderedList>
          ),
          li: ({ children }) => (
            <ListItem fontSize="sm" lineHeight="1.5" mb={0.5}>
              {children}
            </ListItem>
          ),
          br: () => null, // Remove explicit line breaks to prevent extra spacing
          h1: ({ children }) => <Text fontSize="xl" fontWeight="bold" mt={3} mb={2}>{children}</Text>,
          h2: ({ children }) => <Text fontSize="lg" fontWeight="bold" mt={3} mb={2}>{children}</Text>,
          h3: ({ children }) => <Text fontSize="md" fontWeight="bold" mt={2} mb={1}>{children}</Text>,
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // Responsive sidebar width
  const sidebarWidth = useBreakpointValue({
    base: '100%',      // Full width on mobile
    sm: '320px',       // Narrower on small screens
    md: '360px',       // Medium on tablets
    lg: '400px',       // Full width on desktop
    xl: '400px'
  });
  
  const { aiAnswer, aiAnswerStreaming, aiLoading, aiError, aiJournalQueriesRemaining } = useSelector((state) => state.ai);
  const { user } = useSelector((state) => state.user);

  const isFreeUser = user && user.has_paid === 0;
  const quotaExhausted = isFreeUser && aiJournalQueriesRemaining === 0;

  useEffect(() => {
    if (aiAnswer && aiAnswer.answer) {
      // Resolve the most recent pending entry (added at submit time) by filling
      // in the answer/citations. Nothing to do if there's no pending entry —
      // can happen when aiAnswer is hydrated from a previous session.
      setChatHistory(prev => {
        const lastPendingIdx = prev.map(e => e.pending).lastIndexOf(true);
        if (lastPendingIdx === -1) return prev;
        const updated = [...prev];
        updated[lastPendingIdx] = {
          ...updated[lastPendingIdx],
          answer: aiAnswer.answer,
          citations: aiAnswer.citations || [],
          pending: false,
        };
        return updated;
      });

      if (aiError) {
        toast({
          title: "Error",
          description: "Failed to get AI response. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true
        });
      }
    }
  }, [aiAnswer, aiError, toast]);

  useEffect(() => {
    // Scroll to bottom on any chat update: new bubble appended, streaming
    // tokens arriving, or final answer resolving. behavior:'auto' (not
    // 'smooth') because smooth animations get cancelled by subsequent renders
    // during fast token streaming, leaving the view stuck. rAF ensures we
    // measure after the new bubble's layout is committed.
    const id = requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    });
    return () => cancelAnimationFrame(id);
  }, [chatHistory, aiAnswerStreaming, aiLoading]);

  useEffect(() => {
    // Add Escape key handler to collapse chat window (just closes it, button remains)
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !inline) {
        e.preventDefault();
        setIsOpen(false);
      }
    };
    
    if (isOpen && !inline) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, inline]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || aiLoading) return;

    if (!user || !user.user_id) {
      toast({
        title: "Not logged in",
        description: "Please log in to use AI features.",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    const submittedQuestion = question.trim();
    setChatHistory(prev => [...prev, {
      question: submittedQuestion,
      answer: null,
      citations: [],
      timestamp: new Date().toLocaleTimeString(),
      pending: true,
    }]);
    setQuestion('');

    await dispatch(askMyJournalStream({ question: submittedQuestion }));
  };

  const toggleOpen = () => {
    setIsOpen((prev) => {
      if (!prev) setTimeout(() => inputRef.current?.focus(), 100);
      return !prev;
    });
  };

  // TRADE-1009: react-draggable preventDefault()s touchstart, which kills the
  // synthetic click on touch — so the toggle is driven from the drag lifecycle
  // (fires on touch) and onClick only backstops mouse + keyboard.
  const handleDragStart = (e, data) => {
    dragOrigin.current = { x: data.x, y: data.y };
    wasDragged.current = false;
  };

  const handleDragStop = (e, data) => {
    const dragged = isDragGesture(dragOrigin.current, data);
    wasDragged.current = dragged;
    const isTouch = typeof e?.type === 'string' && e.type.startsWith('touch');
    if (isTouch && !dragged) toggleOpen(); // mouse tap is owned by onClick
  };

  const handleToggle = () => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    toggleOpen();
  };

  const handleClear = () => {
    setChatHistory([]);
    setQuestion('');
  };

  // Simple markdown renderer component


  const handleIndexData = async () => {
    setIsIndexing(true);
    try {
      await dispatch(indexUserData({}));
      toast({
        title: "Indexing Complete",
        description: "Your trading data has been indexed successfully! You can now ask questions about your trades and journal entries.",
        status: "success",
        duration: 5000,
        isClosable: true
      });
    } catch (err) {
      toast({
        title: "Indexing Failed",
        description: "Failed to index your data. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsIndexing(false);
    }
  };

  // If sidebar mode, render as a fixed sidebar on the right
  if (sidebar) {
    return (
      <>
        {/* Draggable toggle button */}
        {!isOpen && (
          <Draggable
            onStart={handleDragStart}
            onStop={handleDragStop}
          >
            <Box
              position="fixed"
              bottom="12px"
              right="12px"
              zIndex={1000}
            >
              <Tooltip label="AI Chat" placement="left" hasArrow>
                <IconButton
                  icon={<BsChatDots size="20px" />}
                  onClick={handleToggle}
                  size="lg"
                  colorScheme="blue"
                  borderRadius="full"
                  boxShadow="lg"
                  aria-label="Open AI Chat"
                />
              </Tooltip>
            </Box>
          </Draggable>
        )}

        {/* Chat Panel */}
        {isOpen && (
          <Box
            position="fixed"
            bottom="12px"
            right="12px"
            width={sidebarWidth}
            height="550px"
            bg={colorMode === 'light' ? 'white' : 'gray.800'}
            borderRadius="xl"
            boxShadow="xl"
            zIndex={1000}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            transformOrigin="bottom right"
            sx={{
              '@keyframes chatOpen': {
                from: { opacity: 0, transform: 'scale(0.3) translateY(20px)' },
                to: { opacity: 1, transform: 'scale(1) translateY(0)' },
              },
              animation: 'chatOpen 0.25s ease-out',
            }}
          >
            {/* Header */}
            <Flex
              px={4}
              py={3}
              borderBottomWidth="1px"
              borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
              alignItems="center"
              justifyContent="space-between"
              bg={colorMode === 'light' ? 'white' : 'gray.800'}
            >
              <Text fontWeight="bold" fontSize="lg" color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Ask My Journal</Text>
              <HStack>
                <Button size="xs" onClick={handleClear} variant="ghost" colorScheme="gray">
                  Clear
                </Button>
                <CloseButton onClick={handleToggle} />
              </HStack>
            </Flex>

            {/* Chat History */}
            <Box
              flex="1"
              overflowY="auto"
              p={4}
            >
              {chatHistory.length === 0 ? (
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
                    Ask questions about your trades and journal entries!
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'}>
                    Examples:
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "What was my best trade this month?"
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "Show me all my SPY trades"
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "What did I write in my journal about options?"
                  </Text>
                </VStack>
              ) : (
                <VStack spacing={3} align="stretch">
                  {chatHistory.map((entry, index) => (
                    <Box key={index}>
                      {/* User question — right-aligned blue bubble */}
                      <Box
                        p={3}
                        bg={colorMode === 'light' ? 'blue.500' : 'blue.600'}
                        color="white"
                        borderRadius="lg"
                        borderBottomRightRadius="sm"
                        ml="auto"
                        maxW="80%"
                        mb={2}
                      >
                        <Text fontSize="sm">{entry.question}</Text>
                      </Box>
                      {/* AI answer — left-aligned gray bubble (or spinner while pending) */}
                      <Box
                        p={3}
                        bg={colorMode === 'light' ? 'gray.100' : 'gray.700'}
                        borderRadius="lg"
                        borderBottomLeftRadius="sm"
                        mr="auto"
                        maxW="90%"
                      >
                        {entry.pending ? renderPendingBody() : (
                          <>
                            <Box fontSize="sm">
                              {renderMarkdown(entry.answer)}
                            </Box>
                            {entry.citations && entry.citations.length > 0 && (
                              <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mt={1}>
                                {summarizeSources(entry.citations)}
                              </Text>
                            )}
                          </>
                        )}
                      </Box>
                      <Text fontSize="xs" color={colorMode === 'light' ? 'gray.400' : 'gray.500'} mt={1} ml={1}>
                        {entry.timestamp}
                      </Text>
                    </Box>
                  ))}
                  <div ref={chatEndRef} />
                </VStack>
              )}
            </Box>

            {/* Input */}
            <Box p={4} borderTopWidth="1px" borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
              {quotaExhausted && (
                <Box mb={2} p={2} bg="orange.100" borderRadius="md">
                  <Text fontSize="xs" color="orange.800" fontWeight="bold">
                    You've used all 10 free AI queries. Upgrade to a paid plan for unlimited access.
                  </Text>
                </Box>
              )}
              {isFreeUser && aiJournalQueriesRemaining !== null && aiJournalQueriesRemaining > 0 && (
                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mb={1}>
                  {aiJournalQueriesRemaining}/10 queries remaining
                </Text>
              )}
              <form onSubmit={handleSubmit}>
                <VStack spacing={2}>
                  <HStack width="100%">
                    <Input
                      ref={inputRef}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder={quotaExhausted ? "Upgrade to continue asking..." : "Ask about your trades..."}
                      isDisabled={aiLoading || quotaExhausted}
                      size="sm"
                    />
                    <Button
                      type="submit"
                      colorScheme="blue"
                      size="sm"
                      isDisabled={!question.trim() || aiLoading || quotaExhausted}
                    >
                      {aiLoading ? <Spinner size="xs" /> : 'Send'}
                    </Button>
                  </HStack>
                  {/* Index Data button */}
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={handleIndexData}
                    colorScheme="blue"
                    isLoading={isIndexing}
                    loadingText="Indexing..."
                    width="100%"
                  >
                    Index My Data
                  </Button>
                </VStack>
              </form>
            </Box>
          </Box>
        )}
      </>
    );
  }

  // If inline mode, render a simple button without draggable/fixed positioning
  if (inline) {
    return (
      <>
        <IconButton
          icon={<BsChatDots />}
          onClick={handleToggle}
          size="sm"
          colorScheme="blue"
          borderRadius="md"
          aria-label="Open AI Chat"
        />
        {isOpen && (
          <Box
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="500px"
            maxHeight="600px"
            bg={colorMode === 'light' ? 'white' : 'gray.800'}
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="xl"
            zIndex={1000}
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <Flex
              p={3}
              borderBottomWidth="1px"
              alignItems="center"
              justifyContent="space-between"
              bg={colorMode === 'light' ? 'blue.500' : 'blue.700'}
              color="white"
              borderTopRadius="lg"
            >
              <Text fontWeight="bold">Ask My Journal</Text>
              <HStack>
                <Button size="xs" onClick={handleClear} variant="ghost" color="white">
                  Clear
                </Button>
                <CloseButton onClick={handleToggle} color="white" />
              </HStack>
            </Flex>

            {/* Chat History */}
            <Box
              flex="1"
              overflowY="auto"
              p={4}
              maxHeight="400px"
            >
              {chatHistory.length === 0 ? (
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
                    Ask questions about your trades and journal entries!
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'}>
                    Examples:
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "What was my best trade this month?"
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "Show me all my SPY trades"
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "What did I write in my journal about options?"
                  </Text>
                </VStack>
              ) : (
                <VStack spacing={4} align="stretch">
                  {chatHistory.map((entry, index) => (
                    <Box key={index}>
                      <Box
                        p={2}
                        bg={colorMode === 'light' ? 'blue.50' : 'blue.900'}
                        borderRadius="md"
                        mb={2}
                      >
                        <Text fontSize="sm" fontWeight="bold" mb={1}>
                          Q: {entry.question}
                        </Text>
                        {entry.pending ? renderPendingBody({ py: 1 }) : (
                          <>
                            <Box fontSize="sm">
                              {renderMarkdown(entry.answer)}
                            </Box>
                            {entry.citations && entry.citations.length > 0 && (
                              <Box mt={2}>
                                <Text fontSize="xs" fontWeight="bold" mb={1}>
                                  Sources:
                                </Text>
                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                                  {summarizeSources(entry.citations)}
                                </Text>
                              </Box>
                            )}
                          </>
                        )}
                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mt={1}>
                          {entry.timestamp}
                        </Text>
                      </Box>
                      {index < chatHistory.length - 1 && <Divider />}
                    </Box>
                  ))}
                  <div ref={chatEndRef} />
                </VStack>
              )}
            </Box>

            {/* Input */}
            <Box p={3} borderTopWidth="1px">
              {quotaExhausted && (
                <Box mb={2} p={2} bg="orange.100" borderRadius="md">
                  <Text fontSize="xs" color="orange.800" fontWeight="bold">
                    You've used all 10 free AI queries. Upgrade to a paid plan for unlimited access.
                  </Text>
                </Box>
              )}
              {isFreeUser && aiJournalQueriesRemaining !== null && aiJournalQueriesRemaining > 0 && (
                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mb={1}>
                  {aiJournalQueriesRemaining}/10 queries remaining
                </Text>
              )}
              <form onSubmit={handleSubmit}>
                <HStack>
                  <Input
                    ref={inputRef}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={quotaExhausted ? "Upgrade to continue asking..." : "Ask about your trades..."}
                    isDisabled={aiLoading || quotaExhausted}
                    size="sm"
                  />
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="sm"
                    isDisabled={!question.trim() || aiLoading || quotaExhausted}
                  >
                    {aiLoading ? <Spinner size="xs" /> : 'Send'}
                  </Button>
                </HStack>
              </form>
              {/* Index Data and Close buttons */}
              <HStack mt={2} justifyContent="space-between">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleIndexData}
                  colorScheme="blue"
                  isLoading={isIndexing}
                  loadingText="Indexing..."
                >
                  Index My Data
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleToggle}
                  colorScheme="gray"
                >
                  Close (or press Esc)
                </Button>
              </HStack>
            </Box>
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      {!isOpen ? (
        <Draggable
          onStart={handleDragStart}
          onStop={handleDragStop}
        >
          <Box
            position="fixed"
            bottom="20px"
            right="20px"
            zIndex={1000}
            cursor="move"
          >
            <Tooltip label="AI Chat" placement="left" hasArrow>
              <IconButton
                icon={<BsChatDots />}
                onClick={handleToggle}
                size="lg"
                colorScheme="blue"
                borderRadius="full"
                boxShadow="lg"
                aria-label="Open AI Chat"
              />
            </Tooltip>
          </Box>
        </Draggable>
      ) : (
        <Draggable handle=".drag-handle">
          <Box
            position="fixed"
            bottom="20px"
            right="20px"
            width="400px"
            maxHeight="600px"
            bg={colorMode === 'light' ? 'white' : 'gray.800'}
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="xl"
            zIndex={1000}
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <Flex
              className="drag-handle"
              cursor="move"
              p={3}
              borderBottomWidth="1px"
              alignItems="center"
              justifyContent="space-between"
              bg={colorMode === 'light' ? 'blue.500' : 'blue.700'}
              color="white"
              borderTopRadius="lg"
            >
              <HStack>
                <RiDragMove2Line />
                <Text fontWeight="bold">Ask My Journal</Text>
              </HStack>
              <HStack>
                <Button size="xs" onClick={handleClear} variant="ghost" color="white">
                  Clear
                </Button>
                <CloseButton onClick={handleToggle} color="white" />
              </HStack>
            </Flex>

            {/* Chat History */}
            <Box
              flex="1"
              overflowY="auto"
              p={4}
              maxHeight="400px"
            >
              {chatHistory.length === 0 ? (
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
                    Ask questions about your trades and journal entries!
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'}>
                    Examples:
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "What was my best trade this month?"
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "Show me all my SPY trades"
                  </Text>
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.500'} pl={4}>
                    • "What did I write in my journal about options?"
                  </Text>
                </VStack>
              ) : (
                <VStack spacing={4} align="stretch">
                  {chatHistory.map((entry, index) => (
                    <Box key={index}>
                      <Box
                        p={2}
                        bg={colorMode === 'light' ? 'blue.50' : 'blue.900'}
                        borderRadius="md"
                        mb={2}
                      >
                        <Text fontSize="sm" fontWeight="bold" mb={1}>
                          Q: {entry.question}
                        </Text>
                        {entry.pending ? renderPendingBody({ py: 1 }) : (
                          <>
                            <Box fontSize="sm">
                              {renderMarkdown(entry.answer)}
                            </Box>
                            {entry.citations && entry.citations.length > 0 && (
                              <Box mt={2}>
                                <Text fontSize="xs" fontWeight="bold" mb={1}>
                                  Sources:
                                </Text>
                                <HStack spacing={1} flexWrap="wrap">
                                  {entry.citations.map((citation, idx) => (
                                    <Badge key={idx} colorScheme="green" fontSize="xs">
                                      {citation.type || 'document'}
                                    </Badge>
                                  ))}
                                </HStack>
                              </Box>
                            )}
                          </>
                        )}
                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mt={1}>
                          {entry.timestamp}
                        </Text>
                      </Box>
                      {index < chatHistory.length - 1 && <Divider />}
                    </Box>
                  ))}
                  <div ref={chatEndRef} />
                </VStack>
              )}
            </Box>

            {/* Input */}
            <Box p={3} borderTopWidth="1px">
              {quotaExhausted && (
                <Box mb={2} p={2} bg="orange.100" borderRadius="md">
                  <Text fontSize="xs" color="orange.800" fontWeight="bold">
                    You've used all 10 free AI queries. Upgrade to a paid plan for unlimited access.
                  </Text>
                </Box>
              )}
              {isFreeUser && aiJournalQueriesRemaining !== null && aiJournalQueriesRemaining > 0 && (
                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mb={1}>
                  {aiJournalQueriesRemaining}/10 queries remaining
                </Text>
              )}
              <form onSubmit={handleSubmit}>
                <HStack>
                  <Input
                    ref={inputRef}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={quotaExhausted ? "Upgrade to continue asking..." : "Ask about your trades..."}
                    isDisabled={aiLoading || quotaExhausted}
                    size="sm"
                  />
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="sm"
                    isDisabled={!question.trim() || aiLoading || quotaExhausted}
                  >
                    {aiLoading ? <Spinner size="xs" /> : 'Send'}
                  </Button>
                </HStack>
              </form>
              {/* Index Data and Close buttons */}
              <HStack mt={2} justifyContent="space-between">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleIndexData}
                  colorScheme="blue"
                  isLoading={isIndexing}
                  loadingText="Indexing..."
                >
                  Index My Data
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleToggle}
                  colorScheme="gray"
                >
                  Collapse (or press Esc)
                </Button>
              </HStack>
            </Box>
          </Box>
        </Draggable>
      )}
    </>
  );
}

